import type { CandidateListing, ReviewUnit } from "../../../api/content/curationApi";
import type { Review } from "../../../api/content/reviewDecoders";
import { readableChangeSetTitle } from "../../changeSets/changeSetTitle";

/**
 * A change set as a Markdown document to download (v1's proposal download, which held
 * the explanation and the generated Turtle): title, state, origin, the reasons given in
 * its history, each unit's statements, the possible duplicates and how they were
 * resolved, and the decisions. Pure; names come from `nameOf`.
 */
export function proposalMarkdown(
  review: Review,
  units: readonly ReviewUnit[],
  candidates: CandidateListing | null,
  nameOf: (userId: string) => string,
): string {
  const changeSet = review.changeSet;
  const lines: string[] = [`# ${readableChangeSetTitle(changeSet.title)}`, ""];
  lines.push(`- State: ${changeSet.state}`, `- Head: ${review.head}`, `- Proposed by ${nameOf(changeSet.openedBy)} at ${changeSet.openedAt}`);
  for (const entry of changeSet.originThreadEntries) {
    lines.push(`- Saved from Thread entry ${entry}`);
  }
  const reasons = changeSet.history.filter((entry) => entry.reason.trim() !== "");
  if (reasons.length > 0) {
    lines.push("", "## Explanation and history", "");
    for (const entry of reasons) {
      lines.push(`- ${entry.at} · ${entry.event} · ${nameOf(entry.actor)}: ${entry.reason}`);
    }
  }
  lines.push("", "## Changes", "");
  for (const item of review.items) {
    lines.push(`- ${item.kind} “${item.name}”: ${item.change}`);
  }
  for (const unit of units.filter((entry) => entry.kind === "term")) {
    lines.push("", `### ${unit.term ?? unit.key} (${unit.change ?? "changed"})`, "");
    if (unit.removedTriples.length + unit.addedTriples.length > 0) {
      lines.push("```turtle", ...unit.removedTriples.map((line) => `# removed: ${line}`), ...unit.addedTriples, "```");
    }
  }
  const proposals = candidates?.candidates ?? [];
  if (proposals.length > 0) {
    lines.push("", "## Possible duplicates", "");
    for (const view of proposals) {
      const candidate = view.candidate;
      const resolution = candidate.resolution === null ? "" : `, ${candidate.resolution.kind}${candidate.resolution.reason !== "" ? `: ${candidate.resolution.reason}` : ""}`;
      lines.push(`- ${candidate.term} may duplicate ${candidate.target} (${Math.round(candidate.scores.total * 100)}%): ${candidate.state}${resolution}`);
    }
  }
  if (review.decisions.length > 0) {
    lines.push("", "## Decisions", "");
    for (const decision of review.decisions) {
      lines.push(`- ${decision.at} · ${decision.kind} · ${nameOf(decision.reviewer)}${decision.comment !== "" ? `: ${decision.comment}` : ""}`);
    }
  }
  return `${lines.join("\n")}\n`;
}

/** A file name from the change set's title. */
export function proposalFileName(title: string, extension: string): string {
  const stem = readableChangeSetTitle(title)
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${stem === "" ? "change-set" : stem.slice(0, 60)}.${extension}`;
}

export function downloadText(fileName: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
